import type { LocationType } from '@cuito/types';
import { apiClient } from '../lib/apiClient';
import type { LocationView } from './stock';

/** `GET /shops` 的一行（`04-api-contract.md` §6.1 端点表第 1 行） */
export interface ShopView {
  id: string;
  name: string;
  code: string;
  address: string | null;
  phone: string | null;
  status: string;
  createdAt: string;
}

/**
 * 地点码（`04-api-contract.md` §6.1 `GET /shops/:id/entry-code`）。
 *
 * **注意端点在 `/shops/:id/...` 而不是 `/locations/:id/...`**：`07-roadmap.md:303`
 * 写的是 `GET /locations/:id/entry-code`，与契约冲突，以契约为准（验收脚本里
 * 有一条反向断言：旧路径必须 404，证明它不是别名）。
 */
export interface ShopEntryCodeView {
  locationCode: string;
  entryUrl: string;
  /**
   * 服务端渲染并落盘的二维码图片路径，形如 `/api/files/location_codes/<uuid>.png`。
   *
   * **已含全局 `/api` 前缀**（契约 §1.1），前端原样用于 `<img src>`：不加前缀也不去前缀，
   * 否则会得到 `/api/api/files/...` 或 `/files/...` 两种坏值。`null` 表示服务端尚未生成。
   */
  qrImageUrl: string | null;
  printable: boolean;
}

/**
 * 地点端点（`04-api-contract.md` §7 端点表 1/2/3，以及 §6.1 的门店与地点码）。
 *
 * `LocationView` 的形状由 `./stock` 拥有（Phase 2 的库存页先用了它），
 * 这里只借用类型：把它搬过来会牵动 `StockPage` 与 `WarehousesPage` 的导入，
 * 而这次改动的目标是「加端点」而不是「搬家」。
 */
export const locationsApi = {
  /** `GET /locations`：**裸数组**（契约 §1.3 的非分页例外） */
  list: (params: { type?: LocationType; active?: boolean } = {}) =>
    apiClient.get<LocationView[]>('/locations', { params }),

  get: (id: string) => apiClient.get<LocationView>(`/locations/${id}`),

  /** 改名 / 启停 / 坐标底板行列数（契约 §7 端点表第 3 行，权限 `shop:write`） */
  update: (id: string, body: Record<string, unknown>) =>
    apiClient.patch<LocationView>(`/locations/${id}`, body),

  /** 门店列表（`GET /shops`，裸数组）。地点码端点按 shopId 取，故需要它 */
  listShops: () => apiClient.get<ShopView[]>('/shops'),

  /** 地点码：返回 `locationCode` / `entryUrl` / `printable`（契约 §6.1） */
  getShopEntryCode: (shopId: string) =>
    apiClient.get<ShopEntryCodeView>(`/shops/${shopId}/entry-code`),
};
